import { opsEn } from '@/features/catalog/ops/messages';
import { datEn } from '@/features/catalog/data/messages';
import { catEn } from '@/features/catalog/messages';
import { serEn } from '@/features/serials/messages';
import { colEn } from '@/features/collections/messages';
import { svcEn } from '@/features/services/messages';
import { opacEn } from '@/features/opac/messages';
import { opacMoreEn } from '@/features/opac/messagesMore';
/**
 * English message catalogue (the source of truth for message keys).
 * Keys are flat and dotted: "<area>.<name>". Placeholders use {name}.
 * Add a key here first; hi.ts is typed against this object, so the compiler flags missing translations.
 */
export const en = {
  // common
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.search': 'Search',
  'common.loading': 'Loading',
  'common.retry': 'Try again',
  'common.confirm': 'Confirm',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.actions': 'Actions',
  'common.filter': 'Filter',
  'common.filters': 'Filters',
  'common.clear': 'Clear',
  'common.apply': 'Apply',
  'common.export': 'Export',
  'common.import': 'Import',
  'common.print': 'Print',
  'common.download': 'Download',
  'common.upload': 'Upload',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.more': 'More',
  'common.view': 'View',
  'common.details': 'Details',
  'common.status': 'Status',
  'common.name': 'Name',
  'common.date': 'Date',
  'common.all': 'All',
  'common.none': 'None',
  'common.select': 'Select',
  'common.optional': 'Optional',
  'common.required': 'Required',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.noResults': 'No results found',
  'common.today': 'Today',
  'common.yesterday': 'Yesterday',
  'common.rowsSelected': '{count} selected',
  'common.page': 'Page {page} of {total}',
  'common.showing': 'Showing {from} to {to} of {total}',
  'common.rowsPerPage': 'Rows per page',
  'common.submit': 'Submit',
  'common.continue': 'Continue',
  'common.finish': 'Finish',
  'common.create': 'Create',
  'common.new': 'New',
  'common.refresh': 'Refresh',
  'common.sortBy': 'Sort by {column}',
  'common.breadcrumb': 'Breadcrumbs',
  // navigation
  'nav.home': 'Home',
  'nav.search': 'Search',
  'nav.browse': 'Browse',
  'nav.lists': 'Lists',
  'nav.events': 'Events',
  'nav.rooms': 'Study rooms',
  'nav.courses': 'Course reserves',
  'nav.resources': 'E-resources',
  'nav.account': 'My account',
  'nav.signIn': 'Sign in',
  'nav.signOut': 'Sign out',
  'nav.register': 'Join the library',
  'nav.dashboard': 'Dashboard',
  'nav.circulation': 'Circulation',
  'nav.catalog': 'Catalogue',
  'nav.patrons': 'Patrons',
  'nav.fines': 'Fines and fees',
  'nav.acquisitions': 'Acquisitions',
  'nav.reports': 'Reports',
  'nav.tools': 'Tools',
  'nav.admin': 'Administration',
  'nav.staffConsole': 'Staff console',
  'nav.publicCatalogue': 'Public catalogue',
  'nav.more': 'More',
  'nav.main': 'Main navigation',
  // shell
  'shell.skipToContent': 'Skip to main content',
  'shell.openMenu': 'Open menu',
  'shell.collapseSidebar': 'Collapse sidebar',
  'shell.expandSidebar': 'Expand sidebar',
  'shell.searchEverything': 'Search patrons, items, actions',
  'shell.commandPalette': 'Command palette',
  'shell.notifications': 'Notifications',
  'shell.noNotifications': 'You are all caught up',
  'shell.library': 'Library',
  'shell.switchLibrary': 'Switch library',
  'shell.shortcuts': 'Keyboard shortcuts',
  'shell.profile': 'Profile',
  'shell.language': 'Language',
  'shell.theme': 'Theme',
  'shell.offline': 'You are offline. Changes will not be saved until you reconnect.',
  // theme
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',
  // auth
  'auth.signInTitle': 'Welcome back',
  'auth.staffSignInTitle': 'Staff sign in',
  'auth.login': 'Card number, user ID or email',
  'auth.password': 'Password',
  'auth.signingIn': 'Signing in',
  'auth.forgot': 'Forgot your password?',
  'auth.resetTitle': 'Reset your password',
  'auth.resetSend': 'Send reset link',
  'auth.resetSent': 'If that account exists, a reset link is on its way.',
  'auth.newPassword': 'New password',
  'auth.confirmPassword': 'Confirm new password',
  'auth.passwordsMatch': 'Passwords must match',
  'auth.invalidCredentials': 'The card number or password is incorrect.',
  'auth.sessionExpired': 'Your session has expired. Please sign in again.',
  'auth.noAccount': 'New to the library?',
  'auth.staffNotice': 'This area is for library staff.',
  // opac
  'opac.searchPlaceholder': 'Search titles, authors, subjects or ISBN',
  'opac.heroTitle': 'Find your next great read',
  'opac.heroSubtitle': 'Search the whole collection, reserve in a tap and manage your loans from anywhere.',
  'opac.available': 'Available',
  'opac.unavailable': 'All copies on loan',
  'opac.placeHold': 'Place hold',
  'opac.copies': '{count} copies',
  'opac.byAuthor': 'by {author}',
  'opac.results': '{count} results',
  'opac.newArrivals': 'New arrivals',
  'opac.popularNow': 'Popular right now',
  'opac.dueOn': 'Due {date}',
  'opac.renew': 'Renew',
  'opac.renewAll': 'Renew all',
  'opac.overdue': 'Overdue',
  'opac.avail.available': '{available} of {total} copies available',
  'opac.avail.allOnLoan': 'All {total} copies on loan',
  'opac.avail.notForLoan': 'Not for loan',
  'opac.avail.noCopies': 'No copies available',
  'opac.item.available': 'Available',
  'opac.item.onLoan': 'On loan',
  'opac.item.onHoldShelf': 'On hold shelf',
  'opac.item.inTransit': 'In transit',
  'opac.item.damaged': 'Damaged',
  'opac.item.notForLoan': 'Not for loan',
  'card.status.active': 'Active',
  'card.status.expired': 'Expired',
  'card.status.lost': 'Reported lost',
  'card.status.restricted': 'Restricted',
  // account
  'account.loans': 'Loans',
  'account.holds': 'Holds',
  'account.fines': 'Fines',
  'account.history': 'Reading history',
  'account.lists': 'My lists',
  'account.suggestions': 'Suggestions',
  'account.ill': 'Interlibrary loans',
  'account.events': 'My events',
  'account.profile': 'Profile',
  'account.notifications': 'Notifications',
  'account.preferences': 'Preferences',
  'account.digitalCard': 'Digital library card',
  // kiosk
  'kiosk.title': 'Self checkout',
  'kiosk.scanCard': 'Scan your library card',
  'kiosk.scanBook': 'Scan the books you want to borrow',
  'kiosk.done': 'Done',
  'kiosk.disabled': 'Self-checkout kiosk is currently disabled',
  'kiosk.twoFactor': 'Two-factor authentication required',
  // footer
  'footer.hours': 'Opening hours',
  'footer.contact': 'Contact',
  'footer.about': 'About the library',
  'footer.rights': 'All rights reserved.',
  // errors
  'error.generic': 'Something went wrong. Please try again.',
  'error.notFoundTitle': 'We could not find that page',
  'error.notFoundBody': 'The page may have moved, or the link may be mistyped.',
  'error.forbiddenTitle': 'You do not have access to this',
  'error.forbiddenBody': 'Ask a library administrator to grant the permission you need.',
  'error.network': 'Cannot reach the server. Check your connection.',
  'error.goHome': 'Go to home',
  'error.fieldRequired': 'This field is required',
  // status
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.pending': 'Pending',
  'status.overdue': 'Overdue',
  'status.ready': 'Ready for pickup',
  'status.lost': 'Lost',
  'status.onShelf': 'On shelf',
  'status.checkedOut': 'Checked out',
  // placeholders
  'placeholder.title': 'This screen is being built',
  'placeholder.body': 'The navigation and permissions for this area are final. The screen itself arrives soon.',
  // admin and tools page headers
  'admin.libraries.title': 'Libraries',
  'admin.libraries.description': 'Branches, hours, holidays and library groups.',
  'admin.itemTypes.title': 'Item types',
  'admin.itemTypes.description': 'Loan categories and their defaults.',
  'admin.locations.title': 'Shelving locations',
  'admin.locations.description': 'Where items live inside a branch.',
  'admin.collections.title': 'Collection codes',
  'admin.collections.description': 'Collection groupings for statistics and rules.',
  'admin.rules.title': 'Circulation rules',
  'admin.rules.description': 'Loan periods, renewals and fines by patron category and item type.',
  'admin.preferences.title': 'System preferences',
  'admin.preferences.description': 'Every configurable behaviour of the system.',
  'admin.av.title': 'Authorised values',
  'admin.av.description': 'Controlled vocabularies used across forms.',
  'admin.staff.title': 'Roles and permissions',
  'admin.staff.description': 'Who can do what.',
  'admin.apiClients.title': 'API clients',
  'admin.apiClients.description': 'Credentials for integrations.',
  'admin.webhooks.title': 'Webhooks',
  'admin.webhooks.description': 'Notify other systems when things happen.',
  'admin.audit.title': 'Audit log',
  'admin.audit.description': 'A record of every change made by staff.',
  'admin.system.title': 'System health',
  'admin.system.description': 'Version, jobs, storage and connectivity.',
  'tools.notices.title': 'Notice templates',
  'tools.notices.description': 'Edit the messages patrons receive.',
  'tools.queue.title': 'Message queue',
  'tools.queue.description': 'Outgoing messages and their delivery status.',
  'tools.calendar.title': 'Holidays calendar',
  'tools.calendar.description': 'Days each branch is closed.',
  'tools.news.title': 'News',
  'tools.news.description': 'Announcements for the catalogue and staff console.',
  'tools.cron.title': 'Scheduled jobs',
  'tools.cron.description': 'Recurring background tasks.',
  'tools.uploads.title': 'Uploads',
  'tools.uploads.description': 'Files and images.',
  ...svcEn,
  ...catEn,
  ...serEn,
  ...colEn,
  ...opsEn,
  ...datEn,
  ...opacEn,
  ...opacMoreEn,
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
